import { formattedDate } from '../../../../../../utils/dateUtils';
import { isDefined } from '../../../../../../utils/variables';
import { Design } from '../../../../../../viewer/elements/design';
import { Dxf } from '../../../../../../viewer/elements/dxf';
import { ElementObj } from '../../../../../../viewer/elements/element-obj';
import { Geometry } from '../../../../../../viewer/elements/geometry';
import { Glb } from '../../../../../../viewer/elements/glb';
import { TerrainModel } from '../../../../../../viewer/elements/terrain-model';
import { CellGridVolume } from '../../../../../../viewer/elements/volume/cell-grid-volume';
import { PrismVolume } from '../../../../../../viewer/elements/volume/prism-volume';
import { VolumeCalculationAsset } from '../../../../../../viewer/elements/volume/types';
import { VolumeCalculationType } from '../../volume-calculation-tool/volume-calculation-helper';

export interface VolumeWithAsset {
  id: string;
  selected: boolean;
  asset: VolumeCalculationAsset;
  volume: CellGridVolume | PrismVolume;
  sourceName?: string;
  createdAt: Date;
}

export interface VolumeReportData {
  type: 'prism' | 'cell_grid';
  name: string;
  cut: number;
  fill: number;
  cutArea: number;
  fillArea: number;
  cutPlanArea: number;
  fillPlanArea: number;
  targetAssetName: string;
  sourceName?: string;
}

export const getFormatedDate = () => formattedDate(new Date());

export const getVolumeData = (selectedVolumes: VolumeWithAsset[]): VolumeReportData[] => {
  return selectedVolumes.map((item) => {
    return {
      type: item.volume instanceof PrismVolume ? 'prism' : 'cell_grid',
      name: item.volume.name!,
      cut: item.volume?.cut ?? 0,
      fill: item.volume?.fill ?? 0,
      cutArea: item.volume instanceof PrismVolume ? item.volume.cutArea ?? 0 : 0,
      fillArea: item.volume instanceof PrismVolume ? item.volume.fillArea ?? 0 : 0,
      cutPlanArea: item.volume instanceof PrismVolume ? item.volume.cutPlanArea ?? 0 : 0,
      fillPlanArea: item.volume instanceof PrismVolume ? item.volume.fillPlanArea ?? 0 : 0,
      targetAssetName: item.asset.name,
      sourceName: item.sourceName,
    };
  });
};

/**
 * Create a link and click it do download a file
 */
export const downloadBlob = (
  content: string | Buffer | Blob,
  fileName: string,
  fileType: string,
  encoding?: 'utf-8',
  mimeType?: string,
) => {
  const options = isDefined(encoding)
    ? { type: `${mimeType ?? fileType};charset=${encoding}` }
    : { type: mimeType ?? fileType };
  const blob = content instanceof Blob ? content : new Blob([content], options);
  const a = document.createElement('a');

  a.download = `${fileName}.${fileType}`;
  a.href = URL.createObjectURL(blob);
  a.dataset.downloadurl = [fileType, a.download, a.href].join(':');
  a.click();
};

const getAssetOrder = (asset: VolumeCalculationAsset): number => {
  if (asset instanceof Design) {
    return 1;
  }
  if (asset instanceof Geometry) {
    return 2;
  }
  if (asset instanceof TerrainModel) {
    return 4;
  }
  return 3;
};

export const sortVolumesByDate = (a: VolumeWithAsset, b: VolumeWithAsset) => {
  if (a.createdAt < b.createdAt) {
    return 1;
  }

  return -1;
};

export const sortVolumesByAssetType = (a: VolumeWithAsset, b: VolumeWithAsset) => {
  return getAssetOrder(a.asset) - getAssetOrder(b.asset);
};

export const findSelectedVolumes = (volumesWithAssets: VolumeWithAsset[]) => {
  return volumesWithAssets.filter((volume) => volume.selected);
};

export const getAssetVolumeCalculationDataType = (asset: ElementObj): VolumeCalculationType => {
  if (asset instanceof Geometry) {
    return VolumeCalculationType.Area;
  } else if (asset instanceof Design) {
    return VolumeCalculationType.Design;
  } else if (asset instanceof Dxf) {
    return VolumeCalculationType.Dxf;
  } else if (asset instanceof Glb) {
    return VolumeCalculationType.Glb;
  } else if (asset instanceof TerrainModel) {
    return VolumeCalculationType.TerrainModel;
  }

  return VolumeCalculationType.Area;
};

export const hasSelectedAssets = (volumesWithAssets: VolumeWithAsset[] | undefined) => {
  return isDefined(volumesWithAssets) && findSelectedVolumes(volumesWithAssets).length > 0;
};

export const hasValidReportName = (reportName: string) => {
  return isDefined(reportName) && reportName.length > 3;
};
